import { safeSetItem } from "@/lib/storage/safe-local";
/**
 * Storefront product-view tracker — feeds the Admin Dashboard
 * "Most Viewed Product" widget.
 */
import { useEffect, useState } from "react";
import { track } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";

const KEY = "cb16.tracking.product-views.v1";
const EVT = "cb16:tracking-views";

export type ProductViewCount = { productId: string; slug: string; name: string; views: number; lastAt: string };

function read(): Record<string, ProductViewCount> {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(KEY) ?? "{}") as Record<string, ProductViewCount>; }
  catch { return {}; }
}
function write(next: Record<string, ProductViewCount>) {
  safeSetItem(KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(EVT));
}

export function recordProductView(input: { productId: string; slug: string; name: string }) {
  if (typeof window === "undefined") return;
  const map = read();
  const cur = map[input.productId] ?? { productId: input.productId, slug: input.slug, name: input.name, views: 0, lastAt: "" };
  map[input.productId] = { ...cur, slug: input.slug, name: input.name, views: cur.views + 1, lastAt: new Date().toISOString() };
  write(map);
  track(CIO_EVENTS.PRODUCT_VIEWED, {
    product_id: input.productId,
    product_slug: input.slug,
    product_name: input.name,
  });
}

export function useProductViews(): ProductViewCount[] {
  const [items, setItems] = useState<ProductViewCount[]>(() => Object.values(read()));
  useEffect(() => {
    const sync = () => setItems(Object.values(read()));
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return items.sort((a, b) => b.views - a.views);
}